"use client";

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import type { Target } from "motion/react";
import { useHeaderMotion } from "@/components/motion/header-motion";
import { useHeaderDesign } from "../layouts/HeaderDesignContext";
import { createPortal } from "react-dom";
import { usePathname } from "next/navigation";
import type { MenuOptions, NavEntry } from "@/utils/header-navigation";
import type { MenuPresentation } from "@/utils/header-menu-presentation";
import {
  navigationBreakpoints,
  useMinWidth,
  type NavigationBreakpoint,
} from "@/hooks/useMinWidth";
import { lockHeaderScroll } from "@/utils/header-menu-scroll";
import { setMenuReveal } from "@/utils/menu-reveal";
import NavIcon from "./NavIcon";
import NavigationPanelContent, { panelMode } from "./NavigationPanelContent";

const focusable =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const available = (element: HTMLElement) =>
  !element.closest('[inert],[aria-hidden="true"]') &&
  !!element.getClientRects().length &&
  getComputedStyle(element).visibility !== "hidden";
const headerBounds = (element: HTMLElement | null) => {
  const rect = element?.getBoundingClientRect();
  return {
    top: Math.max(0, rect?.top ?? 0),
    bottom: Math.max(0, rect?.bottom ?? 0),
    height: rect?.height ?? 0,
  };
};

/** A viewport layer with type-specific inherited appearance. Presence owns the closing lifecycle. */
export default function NavigationPanel({
  id,
  options,
  desktopItems,
  mobileItems,
  bp,
  anchor,
  toggle,
  onClose,
  logo,
  actions,
  extras,
  presentation,
  inheritedStyle,
  label = "Menu",
}: {
  id: string;
  options: MenuOptions;
  desktopItems: NavEntry[];
  mobileItems: NavEntry[];
  bp: NavigationBreakpoint;
  anchor: React.RefObject<HTMLElement | null>;
  toggle: React.RefObject<HTMLElement | null>;
  onClose: () => void;
  logo?: React.ReactNode;
  actions?: React.ReactNode;
  extras?: React.ReactNode;
  presentation: MenuPresentation;
  inheritedStyle?: Record<string, string>;
  label?: string;
}) {
  const headerDesign = useHeaderDesign();
  const desktop = useMinWidth(navigationBreakpoints[bp]);
  const path = usePathname();
  const openedAt = useRef(path);
  const panel = useRef<HTMLDivElement>(null);
  const [bounds, setBounds] = useState(() => headerBounds(anchor.current));
  const [companionSpace, setCompanionSpace] = useState(0);
  const { motion, useIsPresent, useReducedMotion } = useHeaderMotion();
  const present = useIsPresent();
  const reduced = useReducedMotion();
  const presentRef = useRef(present);
  presentRef.current = present;
  const fullscreen = options.style === "fullscreen";
  const headerControls =
    !fullscreen &&
    (presentation.kind === "standard" || presentation.kind === "transparent");
  const mode = panelMode(options.style, desktop);
  const top = options.style === "drawer" || fullscreen ? 0 : bounds.bottom;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (path !== openedAt.current) onCloseRef.current();
  }, [path]);
  useLayoutEffect(() => {
    const observed = new WeakSet<Element>();
    const measure = () => {
      const next = headerBounds(anchor.current);
      setBounds((previous) =>
        JSON.stringify(previous) === JSON.stringify(next) ? previous : next,
      );
      const menu = panel.current?.getBoundingClientRect();
      const companions = headerControls
        ? [
            ...document.querySelectorAll<HTMLElement>(
              "[data-re-menu-companion]",
            ),
          ]
        : [];
      const spaces = companions.map((companion) => {
        if (!observed.has(companion)) {
          observer?.observe(companion);
          observed.add(companion);
        }
        const action = companion.getBoundingClientRect();
        return menu &&
          action.left < menu.right &&
          action.right > menu.left &&
          action.height
          ? Math.max(0, window.innerHeight - action.top)
          : 0;
      });
      setCompanionSpace(Math.max(0, ...spaces));
      setMenuReveal(panel.current, toggle.current);
    };
    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(measure)
        : undefined;
    if (anchor.current) observer?.observe(anchor.current);
    if (panel.current) observer?.observe(panel.current);
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("reweb:header-position", measure);
    const mutations =
      typeof MutationObserver !== "undefined"
        ? new MutationObserver(measure)
        : undefined;
    mutations?.observe(document.body, { childList: true, subtree: true });
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("reweb:header-position", measure);
      observer?.disconnect();
      mutations?.disconnect();
    };
  }, [anchor, headerControls, toggle]);

  useLayoutEffect(() => {
    if (headerControls || !present) return;
    const layer = panel.current?.closest(".re-nav-layer");
    const background = [...document.body.children].filter(
      (element): element is HTMLElement =>
        element instanceof HTMLElement &&
        element !== layer &&
        !["SCRIPT", "STYLE", "LINK"].includes(element.tagName),
    );
    const previous = background.map((element) => element.inert);
    background.forEach((element) => {
      element.inert = true;
    });
    return () =>
      background.forEach((element, index) => {
        element.inert = previous[index];
      });
  }, [headerControls, present]);

  useEffect(() => {
    const toggleElement = toggle.current;
    const anchorElement = anchor.current;
    const panelElement = panel.current;
    const unlockScroll = lockHeaderScroll(document.body);
    const elements = () =>
      [
        ...(headerControls
          ? [
              ...(anchor.current?.querySelectorAll<HTMLElement>(focusable) ??
                []),
            ]
          : []),
        ...(panel.current?.querySelectorAll<HTMLElement>(focusable) ?? []),
        ...(headerControls
          ? [
              ...document.querySelectorAll<HTMLElement>(
                `[data-re-menu-companion] :is(${focusable})`,
              ),
            ]
          : []),
      ].filter(available);
    const focusFirst = () => {
      const target =
        [
          ...(panel.current?.querySelectorAll<HTMLElement>(focusable) ?? []),
        ].find(available) ?? toggle.current;
      if (target && available(target)) target.focus();
    };
    focusFirst();
    const onKeyDown = (event: KeyboardEvent) => {
      if (!presentRef.current) return;
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab") return;
      const items = elements();
      if (!items.length) return;
      const first = items[0],
        last = items[items.length - 1],
        active = document.activeElement as HTMLElement | null;
      if (!active || !items.includes(active)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    const onPopState = () => onCloseRef.current();
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("popstate", onPopState);
    return () => {
      unlockScroll();
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("popstate", onPopState);
      const target =
        toggleElement && available(toggleElement)
          ? toggleElement
          : anchorElement?.querySelector<HTMLElement>(
              ".re-nav-bar-item:not([data-overflow]) a[href], .re-nav-bar-item:not([data-overflow]) button",
            );
      if (
        target?.isConnected &&
        (document.activeElement === document.body ||
          panelElement?.contains(document.activeElement))
      )
        target.focus();
    };
  }, [headerControls, toggle, anchor]);

  useLayoutEffect(() => {
    if (!present) {
      if (toggle.current && available(toggle.current)) toggle.current.focus();
      return;
    }
    const active = document.activeElement;
    if (
      panel.current &&
      !panel.current.contains(active) &&
      !anchor.current?.contains(active) &&
      !(active instanceof Element && active.closest("[data-re-menu-companion]"))
    )
      (
        [...panel.current.querySelectorAll<HTMLElement>(focusable)].find(
          available,
        ) ?? toggle.current
      )?.focus();
  }, [desktopItems, mobileItems, desktop, toggle, anchor, present]);

  // Every panel grows out of the menu button and shrinks back into it (setMenuReveal measures the circle).
  const reveal = !reduced;
  const origin = {
    x: options.side === "left" ? "30px" : "calc(100% - 54px)",
    y: `${bounds.top + bounds.height / 2 - top}px`,
  };
  // Custom properties keep the final frame inline; native animation teardown otherwise
  // briefly restores the open clip/opacity before AnimatePresence removes the panel.
  const hidden: Target = reduced
    ? { "--re-menu-opacity": 0 }
    : { "--re-menu-reveal": 0 };
  const shown: Target = reduced
    ? { "--re-menu-opacity": 1 }
    : { "--re-menu-reveal": 1 };
  const surface =
    headerControls && presentation.solid && options.style === "drawer" ? (
      <div
        className="re-nav-header-surface"
        aria-hidden
        style={{ top: bounds.top, height: bounds.height }}
      />
    ) : null;
  return createPortal(
    <div
      className="re-nav-layer"
      data-style={mode}
      data-panel-style={options.style}
      data-side={options.side}
      data-kind={presentation.kind}
      data-header-controls={headerControls || undefined}
      data-closing={!present || undefined}
      style={
        {
          ...headerDesign,
          ...inheritedStyle,
          "--re-nav-top": `${top}px`,
          "--re-menu-header-bottom": `${bounds.bottom}px`,
          "--re-menu-width": presentation.drawerWidth,
          "--re-menu-reveal-x": origin.x,
          "--re-menu-reveal-y": origin.y,
          "--re-menu-companion-space": `${companionSpace}px`,
          "--classic-logo-height": presentation.logoHeight,
          "--classic-header-width": presentation.maxWidth,
        } as CSSProperties
      }
    >
      {presentation.kind !== "standard" && surface}
      {!fullscreen && (
        <motion.div
          className="re-nav-backdrop"
          aria-hidden
          initial={{ "--re-menu-backdrop-opacity": 0 }}
          animate={{ "--re-menu-backdrop-opacity": 1 }}
          exit={{ "--re-menu-backdrop-opacity": 0 }}
          transition={{ duration: reduced ? 0 : 0.22 }}
          onClick={onClose}
        />
      )}
      {presentation.kind === "standard" && surface}
      <motion.div
        ref={panel}
        id={id}
        className="re-nav-panel"
        role="dialog"
        aria-modal={(!headerControls && present) || undefined}
        aria-hidden={!present || undefined}
        inert={!present || undefined}
        aria-label={label}
        data-style={mode}
        data-panel-style={options.style}
        data-kind={presentation.kind}
        data-reveal={reveal || undefined}
        data-groups={desktop ? options.groups : "accordion"}
        data-hover={options.hoverEffect}
        data-hover-marker={options.hoverMarker}
        initial={hidden}
        animate={shown}
        exit={{
          ...hidden,
          transition: { duration: reduced ? 0 : 0.32, ease: [0.4, 0, 1, 1] },
        }}
        transition={{
          duration: reduced ? 0 : 0.5,
          ease: [0.32, 0.72, 0, 1],
        }}
      >
        {(fullscreen || !headerControls) && (
          <div
            className={
              fullscreen ? "re-nav-panel-top" : "re-nav-panel-close-row"
            }
          >
            {fullscreen && logo}
            <button
              type="button"
              className="re-nav-close"
              aria-label="Luk menu"
              onClick={onClose}
            >
              {presentation.kind === "classic" && fullscreen ? (
                <span aria-hidden>×</span>
              ) : (
                <NavIcon name="close" />
              )}
            </button>
          </div>
        )}
        <div className="re-nav-panel-inner">
          {presentation.kind === "floating" && options.style === "drawer" && (
            <p className="re-drawer-caption">Navigation</p>
          )}
          <nav className="re-nav-panel-nav" aria-label={label}>
            <NavigationPanelContent
              options={options}
              desktop={desktop}
              desktopItems={desktopItems}
              mobileItems={mobileItems}
              path={path}
              onNavigate={onClose}
              idPrefix={id}
            />
          </nav>
          {!desktop && actions}
          {extras}
        </div>
      </motion.div>
    </div>,
    document.body,
  );
}
